// src/components/chat/TalentChat.tsx
//
// UI native pour la conversation Chatbase (sans iframe).
// - Appelle la Supabase Edge Function `chatbase-proxy` qui forwarde vers Chatbase
//   avec la clé API stockée en secret côté serveur.
// - Supporte le streaming token-par-token (text/event-stream ou texte brut).
// - Quick prompts cliquables pour amorcer la conversation.
// - Style 100 % brand (gradient #d03484 → #d47254, surfaces #fbfaf8, h2 #30475e).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Send, Loader2, Sparkles, RefreshCw, AlertTriangle } from 'lucide-react';

type ChatRole = 'user' | 'assistant' | 'system';

interface ChatMessage {
  role: ChatRole;
  content: string;
}

export interface TalentChatProps {
  /** Provider : "chatbase" (legacy, via chatbase-proxy) ou "openai" (direct, via openai-chat) */
  provider?: 'chatbase' | 'openai';
  /** Audience pour openai-chat (ignoré si provider="chatbase") */
  audience?: 'candidate' | 'employer';
  /** ID du chatbot Chatbase (requis si provider="chatbase") */
  chatbotId?: string;
  /** Message système optionnel (Chatbase a déjà son propre prompt côté serveur) */
  systemPrompt?: string;
  /** Message d'accueil affiché avant la première interaction */
  welcomeMessage?: string;
  /** Boutons de questions pré-rédigées (recommandé pour amorcer) */
  quickPrompts?: string[];
  /** Placeholder du champ de saisie */
  placeholder?: string;
  /** Hauteur fixe en pixels (défaut : 600) */
  height?: number;
  /** Modèle (défaut : gpt-4o-mini) */
  model?: string;
}

// — URL & clé Supabase publiques (déjà utilisées par le client principal) —
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as
  | string
  | undefined;

const FUNCTIONS_BASE = SUPABASE_URL
  ? `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1`
  : '';

export default function TalentChat({
  provider = 'chatbase',
  audience,
  chatbotId,
  systemPrompt,
  welcomeMessage,
  quickPrompts,
  placeholder = 'Posez votre question...',
  height = 600,
  model = 'gpt-4o-mini',
}: TalentChatProps) {
  const proxyUrl = FUNCTIONS_BASE
    ? provider === 'openai'
      ? `${FUNCTIONS_BASE}/openai-chat`
      : `${FUNCTIONS_BASE}/chatbase-proxy`
    : '';
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    if (systemPrompt) {
      return [{ role: 'system', content: systemPrompt }];
    }
    return [];
  });

  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Filtre les messages affichables (on ne montre pas les system)
  const visibleMessages = useMemo(
    () => messages.filter((m) => m.role !== 'system'),
    [messages],
  );

  const hasUserStarted = visibleMessages.some((m) => m.role === 'user');

  // Auto-scroll vers le bas à chaque update
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Cleanup au démontage
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  async function sendMessage(content: string) {
    const trimmed = content.trim();
    if (!trimmed || isStreaming) return;

    if (!proxyUrl || !SUPABASE_ANON_KEY) {
      setError(
        'Configuration manquante : VITE_SUPABASE_URL ou VITE_SUPABASE_ANON_KEY non définies.',
      );
      return;
    }

    if (provider === 'openai' && !audience) {
      setError(
        'Configuration manquante : prop "audience" requise quand provider="openai".',
      );
      return;
    }

    if (provider === 'chatbase' && !chatbotId) {
      setError(
        'Configuration manquante : prop "chatbotId" requise quand provider="chatbase".',
      );
      return;
    }

    setError(null);

    const nextMessages: ChatMessage[] = [
      ...messages,
      { role: 'user', content: trimmed },
    ];
    setMessages(nextMessages);
    setInput('');
    setIsStreaming(true);

    // Ajouter un message assistant vide qu'on va remplir au fil du stream
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    const controller = new AbortController();
    abortRef.current = controller;

    // Body adapté au provider
    const requestBody =
      provider === 'openai'
        ? {
            audience,
            messages: nextMessages.filter((m) => m.role !== 'system'),
            stream: true,
            model,
            temperature: 0.3,
          }
        : {
            chatbotId,
            messages: nextMessages.filter(
              (m) => m.role !== 'system' || systemPrompt,
            ),
            stream: true,
            model,
            temperature: 0,
          };

    try {
      const res = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Erreur ${res.status} — ${errText.slice(0, 200)}`);
      }

      // Lire le stream
      const reader = res.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        // Pas de stream (réponse JSON classique)
        const json = await res.json();
        const text =
          (typeof json?.text === 'string' && json.text) ||
          (typeof json?.message === 'string' && json.message) ||
          JSON.stringify(json);

        setMessages((prev) => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last?.role === 'assistant') last.content = text;
          return copy;
        });
      } else {
        // Stream : on accumule les chunks décodés
        let acc = '';
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          // Chatbase v1 stream = texte brut (pas SSE "data:")
          // On tente quand même un parsing SSE au cas où
          const sseLines = chunk
            .split('\n')
            .filter((l) => l.startsWith('data: '));

          if (sseLines.length > 0) {
            for (const line of sseLines) {
              const data = line.replace(/^data:\s*/, '').trim();
              if (data === '[DONE]') continue;
              try {
                const parsed = JSON.parse(data);
                acc +=
                  parsed?.text ??
                  parsed?.delta?.content ??
                  parsed?.choices?.[0]?.delta?.content ??
                  '';
              } catch {
                acc += data;
              }
            }
          } else {
            acc += chunk;
          }

          setMessages((prev) => {
            const copy = [...prev];
            const last = copy[copy.length - 1];
            if (last?.role === 'assistant') last.content = acc;
            return copy;
          });
        }
      }
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        // l'utilisateur a annulé : on garde ce qui a été reçu
      } else {
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        // Retirer le message assistant vide en cas d'échec total
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant' && last.content === '') {
            return prev.slice(0, -1);
          }
          return prev;
        });
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    sendMessage(input);
  }

  function handleQuickPrompt(prompt: string) {
    sendMessage(prompt);
  }

  function handleReset() {
    abortRef.current?.abort();
    setMessages(systemPrompt ? [{ role: 'system', content: systemPrompt }] : []);
    setInput('');
    setError(null);
  }

  return (
    <div
      className="flex flex-col rounded-[1.75rem] bg-white ring-1 ring-neutral-200 shadow-sm overflow-hidden"
      style={{ height }}
    >
      {/* Zone messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-5 md:px-7 py-6 bg-[#fbfaf8]"
      >
        {/* État initial : message d'accueil + quick prompts */}
        {!hasUserStarted && (
          <div className="max-w-2xl mx-auto py-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] text-white shadow-md shadow-[#d03484]/20 mb-4">
              <Sparkles className="w-5 h-5" />
            </div>
            {welcomeMessage && (
              <p className="text-base md:text-lg text-[#30475e] font-medium leading-relaxed mb-6">
                {welcomeMessage}
              </p>
            )}

            {quickPrompts && quickPrompts.length > 0 && (
              <>
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3">
                  Démarrer rapidement
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {quickPrompts.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handleQuickPrompt(p)}
                      disabled={isStreaming}
                      className="text-left rounded-2xl bg-white ring-1 ring-neutral-200 hover:ring-[#d03484]/40 hover:shadow-md px-4 py-3 text-sm text-[#30475e] transition-all disabled:opacity-50"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Liste des messages */}
        <div className="space-y-4 max-w-3xl mx-auto">
          {visibleMessages.map((m, i) => (
            <MessageBubble
              key={i}
              role={m.role}
              content={m.content}
              isStreaming={isStreaming && i === visibleMessages.length - 1 && m.role === 'assistant'}
            />
          ))}
        </div>

        {/* Erreur */}
        {error && (
          <div className="max-w-3xl mx-auto mt-4 rounded-2xl bg-red-50 ring-1 ring-red-200 px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-red-700">
              <p className="font-semibold mb-0.5">Une erreur est survenue.</p>
              <p className="text-xs leading-relaxed">{error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Footer : input + reset */}
      <form
        onSubmit={handleSubmit}
        className="flex-shrink-0 border-t border-neutral-200 bg-white px-4 md:px-5 py-3"
      >
        <div className="flex items-center gap-2">
          {hasUserStarted && (
            <button
              type="button"
              onClick={handleReset}
              disabled={isStreaming}
              title="Recommencer la conversation"
              className="flex-shrink-0 w-10 h-10 rounded-xl bg-white ring-1 ring-neutral-200 hover:bg-neutral-50 flex items-center justify-center text-neutral-500 hover:text-[#30475e] transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}

          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder}
            disabled={isStreaming}
            className="flex-1 rounded-xl bg-[#fbfaf8] ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 focus:bg-white px-4 py-2.5 text-sm text-[#30475e] placeholder:text-neutral-400 outline-none transition-all"
          />

          <button
            type="submit"
            disabled={isStreaming || !input.trim()}
            className="flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-4 md:px-5 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {isStreaming ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="hidden md:inline">Réflexion...</span>
              </>
            ) : (
              <>
                <span className="hidden md:inline">Envoyer</span>
                <Send className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// — Bulle de message (user / assistant) —
function MessageBubble({
  role,
  content,
  isStreaming,
}: {
  role: ChatRole;
  content: string;
  isStreaming: boolean;
}) {
  const isUser = role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-2.5 ${
          isUser
            ? 'bg-gradient-to-br from-[#d03484] to-[#d47254] text-white shadow-md shadow-[#d03484]/20'
            : 'bg-white text-[#30475e] ring-1 ring-neutral-200 shadow-sm'
        }`}
      >
        <div
          className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${
            isUser ? 'text-white' : 'text-[#30475e]'
          }`}
        >
          {content || (isStreaming ? '…' : '')}
          {isStreaming && content && (
            <span className="inline-block w-1.5 h-3.5 ml-0.5 bg-current align-middle animate-pulse" />
          )}
        </div>
      </div>
    </div>
  );
}
